import { Fragment } from 'react';

import { TFunction } from 'i18next';
import {
  BrainCircuitIcon,
  CirclePlayIcon,
  LocateFixedIcon,
  RefreshCwOffIcon,
  TypeIcon,
  WandSparklesIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import { iconByType, TYPES } from '@/redux/dialogs/helper';
import { DebugStep, DebugStepSubType, DebugStepType } from '@/shared/types/tryIt';
import { capitalizeFirstLetter } from '@/util/util';

import { StandaloneAmend } from './AmendRows';
import { buildAuthRow } from './AuthStepRows';
import { webhookSeverity } from './buildTurnIssues';
import {
  CONTEXT_CHANGE_LABEL_KEY,
  DetailRowSpec,
  ExpandableRow,
  PlainRow,
  StepLink,
} from './DetailsRow';
import {
  AIResponseStepHead,
  AnswerModelHead,
  ConditionStepHead,
  DialogNodeLink,
  JumpStepHead,
  McpToolStepHead,
  NodeName,
  QuestionStepHead,
  outcomeIconClass,
  TagStepHead,
  useAnswerModelLabel,
  WebhookStepRow,
} from './StepRows';
import { buildToolRows, deferredWorkflowCall, TriggeredNodeDetail } from './ToolsRows';
import { ConversationTurn } from '../../../types/conversation';
import { hasAuthExit } from '../../../utils/authTerminalState';
import { flattenSteps } from '../../../utils/steps';

const SENT_ACTION_TYPES = new Set([
  'image',
  'carousel',
  'webview',
  'video',
  'file',
  'url',
  'survey',
  'audio',
]);
const CALLED_ACTION_TYPES = new Set(['googlesheet', 'email', 'replay']);
const CONVERSATION_ACTION_TYPES = new Set(['pause', 'close', 'handover']);

const getStepType = (step: DebugStep): string => {
  if (step.sub_type === DebugStepSubType.WORKFLOW_TRIGGERED) {
    return 'workflow_triggered';
  }
  if (step.type === DebugStepType.WEBHOOK) {
    return 'webhook';
  }
  if (step.type === DebugStepType.MCP_TOOL) {
    return 'mcp_tool';
  }
  if (step.type === DebugStepType.AI_RESPONSE) {
    return 'ai_response';
  }
  return step.action_type || (step.requisite_id ? 'question' : 'condition');
};

const getStepIcon = (step: DebugStep) =>
  iconByType({ id: getStepType(step), size: 16, type: TYPES.ACTION, stepsMode: true });

const ContextChanges = ({
  t,
  step,
  withLabel = false,
}: {
  t: TFunction;
  step: DebugStep;
  withLabel?: boolean;
}) => {
  const entries = Object.entries(step.context_changes ?? {});
  if (entries.length === 0) {
    return withLabel ? <PlainRow>{t('common.context')}</PlainRow> : null;
  }

  return (
    <PlainRow>
      {t('common.context')}{' '}
      {entries.map(([name, change], index) => (
        <Fragment key={name}>
          <span className="font-medium text-foreground">${name}</span>{' '}
          {t(CONTEXT_CHANGE_LABEL_KEY[change])}
          {index < entries.length - 1 ? ', ' : ''}
        </Fragment>
      ))}
    </PlainRow>
  );
};

const NestedStepRows = ({ rows }: { rows: DetailRowSpec[] }) => (
  <div className="flex flex-col gap-1.5">
    {rows.map((row) => (
      <div key={row.key} className="flex min-w-0 flex-col gap-1 leading-5">
        {row.head}
        {row.body}
      </div>
    ))}
  </div>
);

const triggerLabel = (t: TFunction, turn: ConversationTurn): string | null => {
  if (turn.request?.isFlowResponse) {
    return t('conversation.flow_completed');
  }
  return null;
};

export const buildUnderstandRows = (t: TFunction, turn: ConversationTurn): DetailRowSpec[] => {
  const userText = turn.request?.text;
  const standaloneQuestion = turn.metadata.standaloneQuestion;
  const rephrasedText =
    standaloneQuestion && standaloneQuestion !== userText ? standaloneQuestion : null;
  const label = userText ? null : triggerLabel(t, turn);

  const rows: DetailRowSpec[] = [];

  if (userText || label) {
    rows.push({
      key: 'trigger',
      phase: 'understand',
      icon: <CirclePlayIcon className="h-4 w-4" />,
      head: userText ? (
        <PlainRow>
          {t('try_it.details.user_asked')} <StepLink>{userText}</StepLink>
        </PlainRow>
      ) : (
        <PlainRow>{label}</PlainRow>
      ),
    });
  }

  if (rephrasedText) {
    rows.push({
      key: 'rephrased',
      phase: 'understand',
      icon: <WandSparklesIcon className="h-4 w-4" />,
      head: (
        <StandaloneAmend
          turn={turn}
          footer={
            <>
              {t('try_it.rephrased_by_the_virtual_agent')} <StepLink>{rephrasedText}</StepLink>
            </>
          }
        />
      ),
    });
  }

  return rows;
};

export const buildTextSentRow = (t: TFunction, turn: ConversationTurn): DetailRowSpec | null => {
  const steps = turn.metadata.steps ?? [];
  if (steps.some((step) => step.action_type === 'text')) {
    return null;
  }
  if (!turn.responses.some((response) => response.type === 'text')) {
    return null;
  }

  return {
    key: 'text-sent',
    icon: <TypeIcon className="h-4 w-4" />,
    head: <PlainRow>{t('try_it.details.text_message_sent')}</PlainRow>,
  };
};

export const continuesInsideNode = (turn: ConversationTurn): boolean => {
  if (turn.request?.isFlowResponse) {
    return false;
  }
  const steps = turn.metadata.steps ?? [];
  return steps.length > 0 && steps[0]?.type !== DebugStepType.NODE;
};

export const startingNodeStepOf = (turn: ConversationTurn) => {
  const entryStep = (turn.metadata.steps ?? [])[0];
  return entryStep?.sub_type === DebugStepSubType.STARTING_NODE ? entryStep : undefined;
};

const opensNode = (step: DebugStep) =>
  step.type === DebugStepType.NODE || step.action_type === 'event';

const RESPONSE_ACTION_TYPES = new Set([
  'text',
  'whatsapp_flow',
  'question_set',
  ...SENT_ACTION_TYPES,
]);

const sendsResponse = (step: DebugStep) =>
  Boolean(step.action_type && RESPONSE_ACTION_TYPES.has(step.action_type));

/** A resumed node emits no node step, so only the node stack says we are inside one. */
const startsInsideNode = (turn: ConversationTurn): boolean =>
  (continuesInsideNode(turn) && (turn.metadata.nodesStack?.length ?? 0) > 0) ||
  Boolean(startingNodeStepOf(turn));

const answerComesFromNode = (turn: ConversationTurn): boolean => {
  const steps = turn.metadata.steps ?? [];

  if (steps.some((step) => step.type === DebugStepType.AI_RESPONSE)) {
    return false;
  }

  let insideNode = startsInsideNode(turn);
  let fromNode = false;
  steps.forEach((step) => {
    if (opensNode(step)) {
      insideNode = true;
    }
    if (sendsResponse(step)) {
      fromNode = insideNode;
    }
  });

  return fromNode;
};

const answeredByAuthAgent = (turn: ConversationTurn): boolean =>
  flattenSteps(turn.metadata.steps).some((step) => step.type === DebugStepType.AUTH) &&
  !hasAuthExit(turn);

export const buildAnswerModelRow = (
  t: TFunction,
  turn: ConversationTurn,
  label: string | null
): DetailRowSpec | null => {
  if (turn.metadata.collection?.request_code === undefined) {
    return null;
  }

  if (answerComesFromNode(turn)) {
    return null;
  }

  const author = answeredByAuthAgent(turn) ? t('try_it.auth.agent') : label;
  if (!author) {
    return null;
  }

  return {
    key: 'answer-model',
    icon: <BrainCircuitIcon className="h-4 w-4" />,
    head: <AnswerModelHead label={author} />,
  };
};

export const useAfterAnswerRows = (turn: ConversationTurn): DetailRowSpec[] => {
  const { t } = useTranslation();
  const label = useAnswerModelLabel(turn.metadata.languageModel);

  return [buildAnswerModelRow(t, turn, label), ...buildToolRows(turn, 'moveo')].filter(
    (row) => !!row
  );
};

const nodeIcon = (step?: DebugStep) =>
  step?.sub_type === DebugStepSubType.WORKFLOW_TRIGGERED ? (
    <span className={outcomeIconClass(true)}>
      <McpIcon size={16} />
    </span>
  ) : (
    <LocateFixedIcon size={16} />
  );

const entryNodeIdOf = (turn: ConversationTurn): string | undefined => {
  const steps = turn.metadata.steps ?? [];
  const entryStep = steps[0];
  const nodeId = entryStep?.node_id ?? turn.metadata.triggeredNode?.id;

  const triggeredByAgent = entryStep?.sub_type === DebugStepSubType.WORKFLOW_TRIGGERED;
  if (!nodeId || continuesInsideNode(turn) || (steps.length < 2 && !triggeredByAgent)) {
    return undefined;
  }
  if (entryStep.sub_type === DebugStepSubType.STARTING_NODE) {
    return undefined;
  }
  return nodeId;
};

export const buildNodeRow = (t: TFunction, turn: ConversationTurn): DetailRowSpec | null => {
  const steps = turn.metadata.steps ?? [];
  const entryStep = steps[0];
  const nodeId = entryNodeIdOf(turn);
  const stackName = turn.metadata.nodesStack?.find((node) => node.node_id === nodeId)?.name;

  if (!nodeId) {
    return null;
  }
  const isWorkflowTriggered = entryStep.sub_type === DebugStepSubType.WORKFLOW_TRIGGERED;
  const workflowCall = deferredWorkflowCall(turn);
  // The matched intent's own confidence — the strongest candidate may not be the one that fired.
  const confidence = turn.metadata.intent?.confidence ?? 0;

  return {
    key: 'node',
    icon: nodeIcon(entryStep),
    head: (
      <ExpandableRow
        label={t('try_it.details.show_call_details')}
        head={
          <PlainRow>
            {t('try_it.details.node')}{' '}
            <DialogNodeLink nodeId={nodeId}>
              <NodeName nodeId={nodeId} stackName={stackName} />
            </DialogNodeLink>{' '}
            {t('try_it.triggered')}
            {confidence > 0 && (
              <> · {t('try_it.confidence_percentage', { 0: Math.round(confidence * 100) })}</>
            )}
          </PlainRow>
        }
        detail={
          isWorkflowTriggered && workflowCall && <TriggeredNodeDetail invocation={workflowCall} />
        }
      />
    ),
  };
};

const buildNodeTriggeredRow = (
  t: TFunction,
  turn: ConversationTurn,
  step: DebugStep,
  key: string
): DetailRowSpec => {
  const stackName = turn.metadata.nodesStack?.find((node) => node.node_id === step.node_id)?.name;

  return {
    key,
    icon: nodeIcon(step),
    head: (
      <PlainRow>
        {t('try_it.details.node')}{' '}
        <DialogNodeLink nodeId={step.node_id}>
          <NodeName nodeId={step.node_id} stackName={stackName} />
        </DialogNodeLink>{' '}
        {t('try_it.triggered')}
      </PlainRow>
    ),
  };
};

const buildStepRow = (
  t: TFunction,
  turn: ConversationTurn,
  step: DebugStep,
  key: string,
  index: number
): DetailRowSpec | null => {
  const action = step.action_type;
  const icon = getStepIcon(step);

  if (step.type === DebugStepType.AUTH) {
    return buildAuthRow(turn, step, key);
  }

  if (step.type === DebugStepType.NODE) {
    const startingNodeId = startingNodeStepOf(turn)?.node_id;
    if (index === 0 || (startingNodeId && step.node_id === startingNodeId)) {
      return null;
    }
    return buildNodeTriggeredRow(t, turn, step, key);
  }

  if (step.type === DebugStepType.WEBHOOK || action === 'webhook') {
    // The row's own sentence already reports the fallback, so its text step would repeat it.
    const subSteps = (step.sub_steps ?? []).filter(
      (subStep) => !(step.webhook_error && subStep.action_type === 'text')
    );
    const nestedRows = subSteps
      .map((subStep, subIndex) =>
        buildStepRow(t, turn, subStep, `${key}-sub-${subIndex}`, subIndex + 1)
      )
      .filter((row) => !!row);
    const tintedIcon = <span className={outcomeIconClass(!webhookSeverity(step))}>{icon}</span>;

    return {
      key,
      icon: tintedIcon,
      head: (
        <WebhookStepRow
          step={step}
          steps={turn.metadata.steps ?? []}
          context={turn.metadata.context}
          hasNested={nestedRows.length > 0}
        >
          {nestedRows.length > 0 && <NestedStepRows rows={nestedRows} />}
        </WebhookStepRow>
      ),
    };
  }

  if (step.type === DebugStepType.MCP_TOOL) {
    return {
      key,
      icon,
      head: <McpToolStepHead step={step} />,
      body: <ContextChanges t={t} step={step} />,
    };
  }

  if (step.type === DebugStepType.AI_RESPONSE) {
    return { key, icon, head: <AIResponseStepHead step={step} /> };
  }

  if (step.type === DebugStepType.INFINITE_LOOP) {
    return {
      key,
      icon: <RefreshCwOffIcon size={16} />,
      head: <PlainRow>{t('try_it.details.infinite_loop')}</PlainRow>,
    };
  }

  if (
    step.requisite_id ||
    step.type === DebugStepType.REQUISITE ||
    step.type === DebugStepType.SLOT ||
    step.type === DebugStepType.PREFILL
  ) {
    return { key, icon, head: <QuestionStepHead step={step} /> };
  }

  if (step.type === DebugStepType.CONDITION || (step.condition_id && !action)) {
    return { key, icon, head: <ConditionStepHead step={step} /> };
  }

  if (!action) {
    return null;
  }

  if (action === 'set_variables' || action === 'reset') {
    return { key, icon, head: <ContextChanges t={t} step={step} withLabel /> };
  }

  if (action === 'event') {
    const jumpNodeName = turn.metadata.nodesStack?.find(
      (node) => node.node_id === step.node_id
    )?.name;
    return { key, icon, head: <JumpStepHead step={step} nodeName={jumpNodeName} /> };
  }

  if (action === 'tag') {
    return { key, icon, head: <TagStepHead step={step} /> };
  }

  if (action === 'switch_channel') {
    return { key, icon, head: <PlainRow>{t('actions.types.switch_channel')}</PlainRow> };
  }

  if (CONVERSATION_ACTION_TYPES.has(action)) {
    return {
      key,
      icon,
      head: (
        <PlainRow>
          <span className="capitalize">{t('try_it.details.conversation', { 0: action })}</span>
        </PlainRow>
      ),
    };
  }

  if (action === 'reminder' || action === 'on_inactivity') {
    return {
      key,
      icon,
      head: (
        <PlainRow>
          {t('actions.types.reminder')}
          {step.reminder_seconds ? ` (${step.reminder_seconds}s)` : ''}
        </PlainRow>
      ),
    };
  }

  if (action === 'whatsapp_flow') {
    return { key, icon, head: <PlainRow>{t('try_it.details.whatsapp_flow_sent')}</PlainRow> };
  }

  if (action === 'question_set') {
    return {
      key,
      icon,
      head: (
        <PlainRow>
          {t('try_it.details.action_type_sent', { 0: t('actions.types.question_set') })}
        </PlainRow>
      ),
    };
  }

  if (action === 'text') {
    return { key, icon, head: <PlainRow>{t('try_it.details.text_message_sent')}</PlainRow> };
  }

  if (SENT_ACTION_TYPES.has(action)) {
    return {
      key,
      icon,
      head: (
        <PlainRow>
          {t('try_it.details.action_type_sent', { 0: capitalizeFirstLetter(action) })}
        </PlainRow>
      ),
    };
  }

  if (CALLED_ACTION_TYPES.has(action)) {
    return {
      key,
      icon,
      head: (
        <PlainRow>
          {t('try_it.details.action_type_called', { 0: capitalizeFirstLetter(action) })}
        </PlainRow>
      ),
    };
  }

  return { key, icon, head: <PlainRow>{capitalizeFirstLetter(action)}</PlainRow> };
};

const lastResponseIndexOf = (steps: DebugStep[]) =>
  steps.reduce((last, step, index) => (sendsResponse(step) ? index : last), -1);

export const buildStepRows = (
  t: TFunction,
  turn: ConversationTurn,
  beforeResponse: DetailRowSpec[] = []
): DetailRowSpec[] => {
  let insideNode = Boolean(entryNodeIdOf(turn)) || Boolean(startingNodeStepOf(turn));
  let inserted = false;
  const steps = turn.metadata.steps ?? [];
  const responseIndex = lastResponseIndexOf(steps);

  const rows = steps.reduce<DetailRowSpec[]>((acc, step, index) => {
    const row = buildStepRow(t, turn, step, `step-${index}`, index);
    if (!row) {
      return acc;
    }
    let answerRows: DetailRowSpec[] = [];
    if (index === responseIndex) {
      inserted = true;
      answerRows = beforeResponse.map((answerRow) =>
        insideNode ? { ...answerRow, indented: true } : answerRow
      );
    }
    if (opensNode(step)) {
      insideNode = true;
      return [...acc, ...answerRows, row];
    }
    return [...acc, ...answerRows, insideNode ? { ...row, indented: true } : row];
  }, []);

  return inserted ? rows : [...rows, ...beforeResponse];
};
